import { useState } from 'react';

interface Product {
  description: string;
  includes: string[];
  useCases: string[];
  features: string[];
}

interface ProductDetailBreakdownProps {
  product: Product;
}

export default function ProductDetailBreakdown({ product }: ProductDetailBreakdownProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'includes' | 'uses'>('overview');

  return (
    <section className="py-16 bg-white border-t border-stone-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex gap-1 mb-8 bg-stone-100 p-1 rounded-full w-fit">
          {(['overview', 'includes', 'uses'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap px-6 py-2 rounded-full text-sm font-bold transition-all cursor-pointer capitalize ${activeTab === tab ? 'bg-white text-[#0B5C4D]' : 'text-stone-500 hover:text-stone-700'}`}
            >
              {tab === 'uses' ? 'Use Cases' : tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-stone-900 font-black text-xl mb-4">About This Kit</h3>
              <p className="text-stone-600 leading-relaxed">{product.description}</p>
            </div>
            <div>
              <h3 className="text-stone-900 font-black text-xl mb-4">Key Features</h3>
              <div className="space-y-3">
                {product.features.map((f) => (
                  <div key={f} className="flex items-center gap-3 bg-[#F5EFE6] rounded-xl px-4 py-3">
                    <i className="ri-check-line text-[#0B5C4D] text-base" />
                    <span className="text-stone-700 font-medium text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'includes' && (
          <div className="max-w-xl">
            <h3 className="text-stone-900 font-black text-xl mb-5">What&apos;s in the Box</h3>
            <div className="space-y-3">
              {product.includes.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 border border-stone-100 rounded-xl px-5 py-4">
                  <span className="w-7 h-7 rounded-full bg-[#0B5C4D]/10 text-[#0B5C4D] text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  <span className="text-stone-700 text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'uses' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {product.useCases.map((uc) => (
              <div key={uc} className="bg-[#F5EFE6] rounded-2xl p-5 text-center">
                <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/15 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-tools-line text-[#FF6B35] text-lg" />
                </div>
                <p className="text-stone-800 font-semibold text-sm">{uc}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
